import { isEmpty } from "lodash";
import { action, decorate, observable, runInAction, toJS } from "mobx";
import {
  activeCamapaignFeilds,
  getContactInfo,
  hubspotFieldList,
  importACContacts,
  importHubspotContacts,
  importZohoContacts,
  saveActiveCampToken,
  activeCamapaignFilter,
  saveGHLToken,
  saveHubspotToken,
  saveZohoToken,
  searchContact,
  sendInvoice,
  zohoFields,
  getMessageById,
  removeAccount,
  savePipedriveToken,
  pipedriveFieldList,
  importPipedriveContacts,
  getGHLAccounts,
  assignDefaultSender,
  getGHLAccountStats,
  configureBilling,
  removeGHIntegration,
  sendGHLWebhook,
  saveGHLWebhook,
  reConnectGHLAccount,
  getGHLWebhookLogs,
  resendAnalyticsData,
  generateBillingReport,
  getGHLAccountConnection,
  getBillingReportPreview,
  getDedicatedNumberAnalytics,
  generateDedicatedNumberAnalyticsReport,
  fireTestBillingEvent,
  getSenderToAssign,
  createDomainApproval,
  fetchAllDomainApprovals,
  updateDomainApprovalStatus,
  fetchUserDomainApprovals,
  deleteDomainApproval,
  getDomainToken
} from "service/integration";

class ObservableIntegrationStore {
  contact = undefined;
  response = undefined;
  invoiceLink = undefined;
  zohoFieldList = {
    type: "",
    list: [],
    operator: [
      { label: "Equal", value: "equals" },
      { label: "Not Equal", value: "not_equal" },
      { label: "StartsWith", value: "starts_with" },
      { label: "In", value: "in" }
    ]
  };
  hubspotFieldList = [];
  pipedriveFieldList = [];
  activeCampFields = [];
  ghlAccountList = [];
  activeCampFilter = {};

  total = 0;
  pageSize = 10;
  current = 1;
  search = undefined;
  sortField = "createdAt";
  sortOrder = "descend";

  webhook = undefined;
  senderIdWebhook = undefined;
  afterAuthProcess = {};
  groupName = undefined;
  loading = false;
  ghlLogs = undefined;
  userSpecificLogs = undefined;
  importContactFormProps = undefined;
  senderList = [];

  domainApprovals = [];
  userDomainApprovals = [];
  selectedDomain = null;
  isLoading = false;
  error = null;
  totalCount = 0;
  totalPages = 1;

  setLoading(loading) {
    this.isLoading = loading;
  }

  clearError() {
    this.error = null;
  }

  createDomain = async data => {
    this.setLoading(true);
    this.clearError();

    try {
      const response = await createDomainApproval(data);

      runInAction(() => {
        if (response.status) {
          this.domainApprovals.unshift(response.data);
          this.totalCount += 1;
        }
        this.isLoading = false;
      });

      return response;
    } catch (error) {
      runInAction(() => {
        this.error = error?.response?.data?.message || "Failed to create Domain Approval";
        this.isLoading = false;
      });
      throw error;
    }
  };

  fetchAllDomain = async (params = {}) => {
    this.setLoading(true);
    this.clearError();

    try {
      const response = await fetchAllDomainApprovals(params);
      runInAction(() => {
        if (response.status) {
          this.domainApprovals = response.data.items || [];
          this.totalCount = response.data.total || 0;
          this.current = response.data.current || 1;
          this.totalPages = response.data.totalPages || 1;
        }
        this.isLoading = false;
      });

      return response.data;
    } catch (error) {
      runInAction(() => {
        this.domainApprovals = [];
        this.error = error.response?.data?.message || "Failed to fetch domain approvals";
        this.isLoading = false;
      });
      throw error;
    }
  };

  updateDomainStatus = async (id, status, reason = null) => {
    this.setLoading(true);
    this.clearError();

    try {
      const data = { status };
      if (reason) {
        data.reason = reason;
      }

      const response = await updateDomainApprovalStatus(id, data);

      runInAction(() => {
        if (response.status) {
          //update domain in the list
          const index = this.domainApprovals.findIndex(d => d.id === id);
          if (index !== -1) {
            this.domainApprovals[index] = response.data;
          }

          if (this.selectedDomain?._id === id) {
            this.selectedDomain = response.data;
          }
        }

        this.isLoading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error.response?.data?.message || "Failed to update domain status";
        this.isLoading = false;
      });
      throw error;
    }
  };

  fetchUserDomains = async (params = {}) => {
    this.setLoading(true);
    this.clearError();

    try {
      const response = await fetchUserDomainApprovals(params);
      runInAction(() => {
        if (response.status) {
          this.userDomainApprovals = response.data || [];
        }
        this.isLoading = false;
      });

      return response.data;
    } catch (error) {
      runInAction(() => {
        this.userDomainApprovals = [];
        this.error = error.response?.data?.message || "Failed to fetch user domains";
        this.isLoading = false;
      });
      throw error;
    }
  };

  deleteDomain = async id => {
    this.setLoading(true);
    this.clearError();

    try {
      const response = await deleteDomainApproval(id);

      runInAction(() => {
        if (response?.status) {
          this.domainApprovals = this.domainApprovals.filter(d => d && d._id !== id);
          this.totalCount -= 1;

          if (this.selectedDomain?._id === id) {
            this.selectedDomain = null;
          }
        }
        this.isLoading = false;
      });

      return response;
    } catch (error) {
      runInAction(() => {
        this.error = error.response?.data?.message || "Failed to delete domain";
        this.isLoading = false;
      });
      throw error;
    }
  };

  getDomainToken = async (id, new_token) => {
    this.setLoading(true);
    this.clearError();

    try {
      const response = await getDomainToken(id, new_token.new_token);

      runInAction(() => {
        if (response.success) {
          this.selectedDomain = response.data;
        }
        this.isLoading = false;
      });

      return response;
    } catch (error) {
      runInAction(() => {
        this.selectedDomain = null;
        this.error = error.response?.data?.message || "Failed to fetch domain";
        this.isLoading = false;
      });
      throw error;
    }
  };
  accountStaticsCallback = () => {};

  setField = (field, data) => {
    runInAction(() => {
      this[field] = data;
    });
  };

  changeSorting = fieldObj => {
    runInAction(() => {
      const { field, sortOrder } = fieldObj;
      this.sortField = field;
      this.sortOrder = sortOrder;
    });
  };

  setSearch = search => {
    runInAction(() => {
      this.search = search;
      this.current = 1;
    });
  };

  setPage = current => {
    runInAction(() => {
      this.current = current;
    });
  };

  setPageSize = size => {
    runInAction(() => {
      this.pageSize = size;
      this.current = 1;
    });
  };

  setProps = data => {
    runInAction(() => {
      this.importContactFormProps = data ? data : undefined;
    });
  };

  setData = (field, data, key = undefined) => {
    runInAction(() => {
      if (key) {
        this[field] = {
          ...this[field],
          [key]: data
        };
      } else {
        this[field] = data;
      }
    });
  };

  // SQURE-SPACE

  setInvoiceLink = link => {
    runInAction(() => {
      this.invoiceLink = link ? JSON.parse(JSON.stringify(link)) : undefined;
    });
  };

  searchContact = async mobile => {
    const response = await searchContact(mobile);
    try {
      runInAction(() => {
        this.response = response;
        this.contact = response?.data ? response?.data : undefined;
      });
    } catch (error) {
      runInAction(() => {
        this.response = error;
      });
    }
    return this.response;
  };

  getContactInfo = async mobile => {
    const response = await getContactInfo(mobile);
    try {
      runInAction(() => {
        this.response = response;
        this.contact = response?.data;
      });
    } catch (error) {
      runInAction(() => {
        this.response = error;
      });
    }
    return this.response;
  };

  sendInvoice = async data => {
    const response = await sendInvoice(data);
    try {
      runInAction(() => {
        this.response = response;
        if (response?.data?.invoice_url) {
          this.invoiceLink = response?.data?.invoice_url;
        } else {
          this.invoiceLink = undefined;
        }
      });
    } catch (error) {
      runInAction(() => {
        this.response = error;
      });
    }
    return this.response;
  };

  // OAuth flow
  handleConnect(app, callBack = () => {}, sendData, authWindowRef = null, onFailure = () => {}) {
    let url = "";
    switch (app) {
      case "hubspot":
        url = `https://app.hubspot.com/oauth/authorize?client_id=${process.env.HUBSPOT_CLIENT_ID}&redirect_uri=${process.env.HUBSPOT_REDIRECT_URI}&scope=${process.env.HUBSPOT_SCOPE}`;
        break;
      case "zoho":
        url = `https://${sendData?.accountsURL}/oauth/v2/auth?redirect_uri=${process.env.ZOHO_REDIRECT_URI}&scope=${process.env.ZOHO_SCOPE}&client_id=${process.env.ZOHO_CLIENT_ID}&response_type=code&access_type=offline`;
        break;
      case "ghl":
        url = `https://marketplace.gohighlevel.com/oauth/chooselocation?response_type=code&redirect_uri=${process.env.GHL_REDIRECT_URI}&client_id=${process.env.GHL_CLIENT_ID}&scope=${process.env.GHL_SCOPE}`;
        break;
      case "ac":
        url = `${window.origin}/auth/connect`;
        break;
      case "pipedrive":
        url = `https://oauth.pipedrive.com/oauth/authorize?client_id=${process.env.PIPEDRIVE_CLIENT_ID}&redirect_uri=${process.env.PIPEDRIVE_REDIRECT_URI}&response_type=code`;
        break;
    }

    sessionStorage.setItem("app", app);

    if (sendData) {
      sessionStorage.setItem("params", JSON.stringify(sendData));
    }
    const channel = new BroadcastChannel("window-communication");
    var authWindow = window.open(url, "authWindow", "with=500,height=auto");

    if (authWindowRef) {
      authWindowRef.current = authWindow;
    }

    if (window?.focus) {
      authWindow?.focus();
    }

    let authHandled = false;

    const cleanup = () => {
      authHandled = true;
      channel.close();
      window.removeEventListener("message", messageHandler);
    };

    const messageHandler = event => {
      if (event.data === "authSuccess" || event.data?.ghl) {
        authHandled = true;
        callBack(app, event.data);
        authWindow?.close();
        cleanup();
      }
    };

    window.addEventListener("message", messageHandler);

    channel.onmessage = event => {
      if (event.data === "authSuccess") {
        if (authWindow) {
          callBack(app, event.data);
          authWindow.close();
          cleanup();
        }
      }
    };

    const checkClosed = setInterval(() => {
      if (authWindow?.closed && !authHandled) {
        clearInterval(checkClosed);
        cleanup();
        if (app === "hubspot" && typeof onFailure === "function") {
          onFailure();
        }
      }
    }, 500);
  }

  importContacts = async (app, data) => {
    let response = undefined;

    switch (app) {
      case "hubspot":
        response = await importHubspotContacts(data);
        break;
      case "zoho":
        response = await importZohoContacts(data);
        break;
      case "ac":
        response = await importACContacts(data);
        break;
      case "pipedrive":
        response = await importPipedriveContacts(data);
    }

    try {
      runInAction(() => {
        this.response = response;
      });
    } catch (error) {
      runInAction(() => {
        this.response = error;
      });
    }
    return this.response;
  };

  // HUBSPOT

  saveHubspotToken = async data => {
    const response = await saveHubspotToken(data);
    try {
      runInAction(() => {
        this.response = response;
      });
    } catch (error) {
      runInAction(() => {
        this.response = error;
      });
    }
    return this.response;
  };

  savePipedriveToken = async data => {
    const response = await savePipedriveToken(data);
    try {
      runInAction(() => {
        this.response = response;
      });
    } catch (error) {
      runInAction(() => {
        this.response = error;
      });
    }
    return this.response;
  };

  hubspotFields = async data => {
    const response = !isEmpty(this.hubspotFieldList) ? { data: this.hubspotFieldList } : await hubspotFieldList(data);
    try {
      runInAction(() => {
        this.hubspotFieldList = response?.data;
      });
    } catch (error) {
      runInAction(() => {
        this.response = error;
      });
    }
    return this.hubspotFieldList;
  };

  pipedriveFields = async data => {
    const response = !isEmpty(this.pipedriveFieldList) ? { data: this.pipedriveFieldList } : await pipedriveFieldList(data);
    try {
      runInAction(() => {
        this.pipedriveFieldList = response?.data;
      });
    } catch (error) {
      runInAction(() => {
        this.response = error;
      });
    }

    return this.pipedriveFieldList;
  };

  getMessageById = async data => {
    const response = await getMessageById(data);

    try {
      runInAction(() => {
        this.response = response;
      });
    } catch (error) {
      runInAction(() => {
        this.response = error;
      });
    }

    return this.response;
  };

  // ZOHO

  zohoFields = async type => {
    this.loading = true;
    // const response = this.zohoFieldList.list.length > 0 ? { data: this.zohoFieldList.list } : await zohoFields(type);
    const response = await zohoFields(type);

    try {
      runInAction(() => {
        this.zohoFieldList.list = response?.data;
        this.loading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.response = error;
        this.loading = false;
      });
    }
    return this.zohoFieldList.list;
  };

  saveZohoToken = async data => {
    const response = await saveZohoToken(data);
    try {
      runInAction(() => {
        this.response = response;
      });
    } catch (error) {
      runInAction(() => {
        this.response = error;
      });
    }
    return this.response;
  };

  // GHL

  getSenderToAssign = async () => {
    const response = await getSenderToAssign();
    try {
      runInAction(() => {
        this.senderList = response?.data || [];
      });
    } catch (error) {
      console.log("sender id list", error);
      runInAction(() => {
        this.senderList = error;
      });
    }
    return this.senderList;
  };

  getGHLAccounts = async args => {
    const params = {
      page: this.current,
      sortField: this.sortField,
      sortOrder: this.sortOrder,
      limit: this.pageSize,
      ...(args && args)
    };
    if (this.search) params.search = this.search;
    const response = await getGHLAccounts(params);
    try {
      runInAction(() => {
        const { items, total, current, limit: pageSize, webhook, senderIdWebhook } = response;
        this.webhook = webhook;
        this.senderIdWebhook = senderIdWebhook;
        this.ghlAccountList = items || [];
        this.pageSize = pageSize;
        this.current = current;
        this.total = total;
      });
    } catch (error) {
      runInAction(() => {
        this.response = error;
      });
    }
    return this.ghlAccountList;
  };

  updateGHLAccountStatus = data => {
    if (isEmpty(data) || this.ghlAccountList.length === 0) return;

    const index = this.ghlAccountList.findIndex(item => item?._id === data?._id);

    runInAction(() => {
      if (index !== -1) {
        const newList = [...toJS(this.ghlAccountList)];
        newList[index] = data;

        this.ghlAccountList = newList;
      } else {
        if (this.ghlAccountList.length < this.pageSize) {
          this.ghlAccountList.push(data);
        }
      }
    });
  };

  reConnectGHLAccount = async id => {
    const response = await reConnectGHLAccount(id);
    try {
      runInAction(() => {
        this.response = response;
      });
    } catch (error) {
      runInAction(() => {
        this.response = error;
      });
    }
    return this.response;
  };

  saveGHLToken = async data => {
    const response = await saveGHLToken(data);
    try {
      runInAction(() => {
        this.response = response;
      });
    } catch (error) {
      runInAction(() => {
        this.response = error;
      });
    }
    return this.response;
  };

  assignDefaultSender = async (id, data) => {
    this.loading = true;
    const response = await assignDefaultSender(id, data);
    try {
      runInAction(() => {
        this.response = response;
        this.loading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.response = error;
        this.loading = false;
      });
    }
    return this.response;
  };

  removeGHIntegration = async params => {
    const response = await removeGHIntegration({ subAccount: params });
    try {
      runInAction(() => {
        this.response = response;
      });
    } catch (error) {
      runInAction(() => {
        this.response = error;
      });
    }
    return this.response;
  };

  getGHLAccountStats = async () => {
    const response = await getGHLAccountStats();
    try {
      runInAction(() => {
        if (response?.status) {
          this.response = response?.data;
        } else {
          this.response = {};
        }
      });
    } catch (error) {
      runInAction(() => {
        this.response = {};
      });
    }
    return this.response;
  };

  getGHLAccountConnection = async () => {
    const response = await getGHLAccountConnection();
    try {
      runInAction(() => {
        if (response?.status) {
          this.response = response?.data;
        } else {
          this.response = {};
        }
      });
    } catch (error) {
      runInAction(() => {
        this.response = {};
      });
    }

    return this.response;
  };

  getGHLWebhookLogs = async (subAccountId, page = 1) => {
    if (!subAccountId) {
      console.error("subAccountId is required");
      return null;
    }

    try {
      const response = await getGHLWebhookLogs(subAccountId, page);

      runInAction(() => {
        if (response?.status) {
          this.userSpecificLogs = response?.data;
        } else {
          this.userSpecificLogs = undefined;
        }
      });

      return response?.data;
    } catch (error) {
      console.error("Error fetching webhook logs:", error);
      runInAction(() => {
        this.userSpecificLogs = undefined;
      });
      return null;
    }
  };

  resendAnalyticsData = async (subAccount, webhookLogId = null) => {
    try {
      const response = await resendAnalyticsData(subAccount, webhookLogId);
      return response;
    } catch (error) {
      console.error("Error resending analytics data:", error);
      throw error;
    }
  };

  generateBillingReport = async (webhookId, from, to, type, billingEmail, gstOptions = {}) => {
    try {
      const response = await generateBillingReport(webhookId, {
        from,
        to,
        type,
        ...(billingEmail ? { billingEmail, eventEmail: billingEmail } : {}),
        gstEnabled: Boolean(gstOptions.gstEnabled),
        gstPercent: gstOptions.gstPercent ?? 10
      });
      return response;
    } catch (error) {
      console.error("Error generating billing report:", error);
      throw error;
    }
  };

  getBillingReportPreview = async (subAccount, payload) => {
    try {
      const response = await getBillingReportPreview(subAccount, payload);
      runInAction(() => {
        if (response?.status) {
          this.response = response;
        } else {
          this.response = {};
        }
      });
      return response;
    } catch (error) {
      runInAction(() => {
        this.response = {};
      });
      throw error;
    }
  };

  getDedicatedNumberAnalytics = async (subAccount, payload = {}) => {
    try {
      const response = await getDedicatedNumberAnalytics(subAccount, payload);
      runInAction(() => {
        if (response?.status) {
          this.response = response;
        } else {
          this.response = {};
        }
      });
      return response;
    } catch (error) {
      runInAction(() => {
        this.response = {};
      });
      throw error;
    }
  };

  generateDedicatedNumberAnalyticsReport = async (webhookId, payload = {}) => {
    try {
      const response = await generateDedicatedNumberAnalyticsReport(webhookId, payload);
      return response;
    } catch (error) {
      console.error("Error generating billing report:", error);
      throw error;
    }
  };

  fireTestBillingEvent = async (subAccount, payload) => {
    try {
      const response = await fireTestBillingEvent(subAccount, payload);
      return response;
    } catch (error) {
      console.error("Error firing test billing event:", error);
      throw error;
    }
  };

  configureBilling = async (id, data) => {
    const response = await configureBilling(id, data);
    try {
      runInAction(() => {
        this.response = response;
      });
    } catch (error) {
      runInAction(() => {
        this.response = error;
      });
    }
    return this.response;
  };

  sendGHLWebhook = async data => {
    const response = await sendGHLWebhook(data);
    try {
      runInAction(() => {
        this.response = response;
      });
    } catch (error) {
      runInAction(() => {
        this.response = error;
      });
    }
    return this.response;
  };

  saveGHLWebhook = async data => {
    const response = await saveGHLWebhook(data);
    try {
      runInAction(() => {
        this.response = response;
      });
    } catch (error) {
      runInAction(() => {
        this.response = error;
      });
    }
    return this.response;
  };

  // ACTIVE CAMPAIGN

  saveActiveCampToken = async data => {
    const response = await saveActiveCampToken(data);
    try {
      runInAction(() => {
        this.response = response;
      });
    } catch (error) {
      runInAction(() => {
        this.response = error;
      });
    }
    return this.response;
  };

  activeCampFetchFilter = async data => {
    const response = Object.entries(this.activeCampFilter).length > 0 ? { data: this.activeCampFilter } : await activeCamapaignFilter(data);
    try {
      runInAction(() => {
        this.activeCampFilter = response?.data;
      });
    } catch (error) {
      runInAction(() => {
        this.response = error;
      });
    }
    return this.activeCampFilter;
  };

  activeCampFetchFields = async data => {
    const response = this.activeCampFields.length > 0 ? { data: this.activeCampFields } : await activeCamapaignFeilds(data);
    try {
      runInAction(() => {
        this.activeCampFields = response?.data;
      });
    } catch (error) {
      runInAction(() => {
        this.response = error;
      });
    }
    return this.activeCampFields;
  };

  removeConnectedAccount = async data => {
    const response = await removeAccount(data);
    try {
      runInAction(() => {
        this.response = response;
      });
    } catch (error) {
      runInAction(() => {
        this.response = error;
      });
    }
    return this.response;
  };
}

decorate(ObservableIntegrationStore, {
  contact: observable,
  activeCampFilter: observable,
  activeCampFields: observable,
  invoiceLink: observable,
  hubspotFieldList: observable,
  zohoFieldList: observable,
  importContactFormProps: observable,
  loading: observable,
  pipedriveFieldList: observable,
  total: observable,
  pageSize: observable,
  current: observable,
  search: observable,
  sortField: observable,
  sortOrder: observable,
  webhook: observable,
  afterAuthProcess: observable,
  senderIdWebhook: observable,
  accountStaticsCallback: observable,
  ghlAccountList: observable,
  senderList: observable,
  domainApprovals: observable,
  userDomainApprovals: observable,
  selectedDomain: observable,
  isLoading: observable,
  error: observable,
  totalCount: observable,
  // currentPage: observable,
  totalPages: observable,

  configureBilling: action,
  setField: action,
  updateGHLAccountStatus: action,

  setLoading: action,
  setError: action,
  clearError: action,
  createDomain: action,
  fetchAllDomains: action,
  updateDomainStatus: action,
  fetchUserDomains: action,
  deleteDomain: action
});

export default new ObservableIntegrationStore();
